'use client'

import { createContext, memo, type PropsWithChildren, useEffect, useMemo, useState } from 'react'

import { useAnalyticsConsentContext } from '@/components/providers/AnalyticsConsentProvider'
import { loadSpindl } from '@/components/providers/SpindlProvider'
import { clientEnvs } from '@/config'
import { useWeb3Context } from '@/contexts/Web3Context'

export interface LayoutContextProps {
  isMobileLeftPanelOpen: boolean
  setMobileLeftPanelOpen: (open: boolean) => void
  isLeftPanelExpanded: boolean
  setIsLeftPanelExpanded: (open: boolean) => void
}

const LayoutContext = createContext<LayoutContextProps | null>(null)

export type LayoutContextProviderProps = PropsWithChildren

const LayoutContextProvider = ({ children }: LayoutContextProviderProps) => {
  const [isMobileLeftPanelOpen, setMobileLeftPanelOpen] = useState(false)
  const [isLeftPanelExpanded, setIsLeftPanelExpanded] = useState(true)

  const { account } = useWeb3Context()
  const { isAnalyticsEnabled } = useAnalyticsConsentContext()

  // Add address to spindl tracking, unless the user opted out of analytics.
  useEffect(() => {
    if (!clientEnvs.NEXT_PUBLIC_SPINDL_SDK_KEY || !isAnalyticsEnabled || !account?.address) return

    const address = account.address
    let isActive = true

    const attribute = async () => {
      const sdk = await loadSpindl()
      if (sdk && isActive) await sdk.attribute(address)
    }
    void attribute()

    return () => {
      isActive = false
    }
  }, [account?.address, isAnalyticsEnabled])

  const value = useMemo(
    () => ({
      isMobileLeftPanelOpen,
      setMobileLeftPanelOpen,
      isLeftPanelExpanded,
      setIsLeftPanelExpanded
    }),
    [isMobileLeftPanelOpen, isLeftPanelExpanded]
  )
  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
}

export default memo(LayoutContextProvider)
